import React from "react";
import HeadNext from "next/head";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { removeTags } from "@/shared/utils/content/content";
import {titleShortDefault} from "@/shared/utils/metaData/metaData.constants";
import {makeMetaDataBook} from "@/shared/utils/metaData/metaData.book";
import {makeMetaDataCommon} from "@/shared/utils/metaData/metaData.common";
import {tWithPrefixHandler} from "@/shared/utils/multilang/i18n";

const Head = ({
  pageTitle,

  // To generate page individual metadata pass either props below
  description, keywords, url,
  // or one of the props of a line below
  book, bookMp3,
  folder,
  // ===================================

  children,
}) => {
  const { t } = useTranslation();
  const { basePath } = useRouter();
  const pageTitleFixed = removeTags(tWithPrefixHandler(t, pageTitle, {
    // prevent labels with a colon (":")
    // being considered as the {[namespace]:[key]}
    nsSeparator: false
  }), " ");
  const pageTitleFinal = pageTitleFixed
    ? `${pageTitleFixed} | ${titleShortDefault}` : titleShortDefault;

  const jsxMetaTagsFavIcon = (
    <>
      <link rel="shortcut icon" href={basePath + "/favicon.ico"} />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href={basePath + "/images/icons/next/favicon16.png"}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href={basePath + "/images/icons/next/favicon32.png"}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="96x96"
        href={basePath + "/images/icons/next/favicon96.png"}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="192x192"
        href={basePath + "/images/icons/next/favicon192.png"}
      />
    </>
  );

  const jsxMetaTagsStandAlone = (
    <>
      {/* the ability to install web applications on the desktop and run them in full screen mode*/}
      {/* for ios   */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      {/* for chrome  */}
      <meta name="mobile-web-app-capable" content="yes" />
    </>
  );

  const jsxMetaTagsWindows8ModernUITags = (
    <>
      {/* Lets a user pin the website icon on Windows 8 Modern UI */}
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta
        name="msapplication-TileImage"
        content={basePath + "/images/icons/ms-icon-144x144.png"}
      />
    </>
  );

  /**
   * @type {MetaData}
   */
  const metaData = [];
  if (book) {
    makeMetaDataBook(metaData, book, bookMp3, url);
  } else {
    makeMetaDataCommon(metaData, pageTitleFinal, description, keywords, url);
  }

  const jsxMetaTagsPage = metaData.map((itemMetaData) => {
    const props = {
      [itemMetaData.keyPropKey]: itemMetaData.key,
      [itemMetaData.keyPropValue]: itemMetaData.value,
    };
    return <meta key={itemMetaData.key} {...props}/>;
  });

  return (
    <HeadNext>
      <title>{pageTitleFinal}</title>
      {jsxMetaTagsFavIcon}
      {jsxMetaTagsWindows8ModernUITags}
      {jsxMetaTagsStandAlone}
      {jsxMetaTagsPage}
      {children}
    </HeadNext>
  );
};

Head.propTypes = {
  pageTitle: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  url: PropTypes.string.isRequired,

  book: PropTypes.object,
  bookMp3: PropTypes.string,

  folder: PropTypes.object,

  children: PropTypes.any,
};

export default Head;
